import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Browser speech-to-text (Chrome / Edge / Safari). Appends each finalized utterance via `onFinalTranscript`.
 */
export function useSpeechDictation(onFinalTranscript: (text: string) => void) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const callbackRef = useRef(onFinalTranscript);
  callbackRef.current = onFinalTranscript;

  useEffect(() => {
    const win = window as Window &
      typeof globalThis & {
        SpeechRecognition?: { new (): SpeechRecognition };
        webkitSpeechRecognition?: { new (): SpeechRecognition };
      };
    const Ctor = win.SpeechRecognition ?? win.webkitSpeechRecognition;
    if (!Ctor) {
      setIsSupported(false);
      return;
    }
    setIsSupported(true);
    const recognition = new Ctor();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const text = event.results[0]?.[0]?.transcript?.trim();
      if (text) callbackRef.current(text);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    return () => {
      try {
        recognition.abort();
      } catch {
        /* noop */
      }
    };
  }, []);

  const toggleListening = useCallback(() => {
    const r = recognitionRef.current;
    if (!r) return;
    if (isListening) {
      r.stop();
      return;
    }
    setIsListening(true);
    try {
      r.start();
    } catch {
      setIsListening(false);
    }
  }, [isListening]);

  return { isListening, isSupported, toggleListening };
}
